import React, { useState, useEffect } from 'react';
import { X, Smartphone, Download, Home, Share, PlusSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent } from './ui/dialog';

// Role-specific messaging
const roleMessages = {
  investor: {
    title: "Never Miss a Deal!",
    subtitle: "Add NextFlip to your home screen",
    benefits: [
      "Get instant notifications on new deals",
      "Quick access to the marketplace",
      "Browse deals on the go"
    ],
    cta: "Find deals faster than your competition"
  },
  wholesaler: {
    title: "Close Deals Faster!",
    subtitle: "Add NextFlip to your home screen",
    benefits: [
      "Post deals in seconds from anywhere",
      "Get instant buyer inquiries",
      "Manage your deals on the go"
    ],
    cta: "Your pocket deal machine"
  },
  agent: {
    title: "Serve Your Investors Better!",
    subtitle: "Add NextFlip to your home screen",
    benefits: [
      "Access deal flow for your clients",
      "Connect with active investors",
      "Stay ahead of the market"
    ],
    cta: "Your competitive edge in real estate"
  },
  provider: {
    title: "Grow Your Business!",
    subtitle: "Add NextFlip to your home screen",
    benefits: [
      "Get leads from active investors",
      "Manage inquiries on the go",
      "Build your reputation"
    ],
    cta: "Connect with investors who need your services"
  }
};

// Detect iOS
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Detect if standalone (already installed)
const isStandalone = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         window.navigator.standalone === true;
};

export const AddToHomePopup = ({ isOpen, onClose, userRole = 'investor' }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  
  const messages = roleMessages[userRole] || roleMessages.investor;

  useEffect(() => {
    // Listen for the beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS()) {
      setShowIOSInstructions(true);
      return;
    }

    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        onClose();
      }
      setDeferredPrompt(null);
    } else {
      // Fallback for browsers that don't support beforeinstallprompt
      setShowIOSInstructions(true);
    }
  };

  // Don't show if already installed
  if (isStandalone()) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        {!showIOSInstructions ? (
          <>
            {/* Header with gradient */}
            <div className="bg-gradient-to-br from-[#5DADE2] to-blue-600 p-6 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{messages.title}</h2>
              <p className="text-blue-100">{messages.subtitle}</p>
            </div>

            {/* Benefits */}
            <div className="p-6">
              <ul className="space-y-3 mb-6">
                {messages.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-slate-700">{benefit}</span>
                  </li>
                ))}
              </ul>

              <p className="text-sm text-slate-500 text-center mb-6 italic">
                {messages.cta}
              </p>

              <div className="space-y-3">
                <Button 
                  onClick={handleInstallClick}
                  className="w-full bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full h-12 text-lg"
                  data-testid="add-to-home-btn"
                >
                  <Download className="h-5 w-5 mr-2" />
                  Add to Home Screen
                </Button>
                
                <button 
                  onClick={onClose}
                  className="w-full text-slate-500 hover:text-slate-700 text-sm py-2"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* iOS Instructions */}
            <div className="p-6">
              <button onClick={() => setShowIOSInstructions(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
              
              <h3 className="text-xl font-bold text-slate-900 mb-4 text-center">
                Add to Home Screen
              </h3>
              
              {isIOS() ? (
                <div className="space-y-4">
                  <p className="text-slate-600 text-center mb-4">Follow these steps:</p>
                  
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-[#5DADE2] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="font-medium text-slate-900">Tap the Share button</p>
                      <p className="text-sm text-slate-500">At the bottom of your screen</p>
                      <Share className="h-6 w-6 text-[#5DADE2] mt-2" />
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-[#5DADE2] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="font-medium text-slate-900">Tap "Add to Home Screen"</p>
                      <p className="text-sm text-slate-500">Scroll down in the menu</p>
                      <PlusSquare className="h-6 w-6 text-[#5DADE2] mt-2" />
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-[#5DADE2] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="font-medium text-slate-900">Tap "Add"</p>
                      <p className="text-sm text-slate-500">NextFlip will appear on your home screen!</p>
                      <Home className="h-6 w-6 text-[#5DADE2] mt-2" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-slate-600 text-center mb-4">Follow these steps:</p>
                  
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-[#5DADE2] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">1</div>
                    <div>
                      <p className="font-medium text-slate-900">Tap the menu icon</p>
                      <p className="text-sm text-slate-500">Three dots at the top right</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-[#5DADE2] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">2</div>
                    <div>
                      <p className="font-medium text-slate-900">Tap "Add to Home screen"</p>
                      <p className="text-sm text-slate-500">Or "Install app"</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-[#5DADE2] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">3</div>
                    <div>
                      <p className="font-medium text-slate-900">Confirm</p>
                      <p className="text-sm text-slate-500">NextFlip will be on your home screen!</p>
                    </div>
                  </div>
                </div>
              )}
              
              <Button 
                onClick={onClose}
                className="w-full mt-6 bg-[#5DADE2] hover:bg-blue-600 text-white rounded-full"
              >
                Got it!
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AddToHomePopup;
